import { useState, useCallback, useRef } from 'react'
import { CommentAnchorType } from '@marketing-milk/interfaces'
import { CommentAnchor } from 'components/comments/CommentThread'

export const KNOWN_TYPES: CommentAnchorType[] = [
  'section',
  'narrative-block',
  'performance-metric',
  'creative',
]

// Parse + validate a Slack comment deep-link (`?anchorType=&anchorId=`). Returns
// null when the params are absent or the type isn't one we recognize, so callers
// get the same `KNOWN_TYPES` guard the click handler applies.
export const readCommentDeepLink = (
  search: string = window.location.search
): { anchorType: CommentAnchorType; anchorId: string } | null => {
  const params = new URLSearchParams(search)
  const anchorType = params.get('anchorType')
  const anchorId = params.get('anchorId')
  if (!anchorType || !anchorId) return null
  if (!KNOWN_TYPES.includes(anchorType as CommentAnchorType)) return null
  return { anchorType: anchorType as CommentAnchorType, anchorId }
}

export const useCommentAnchors = () => {
  const [anchor, setAnchor] = useState<CommentAnchor | null>(null)
  const [open, setOpen] = useState(false)
  // Populated by `setRoot`; exposed for consumers that need the scroll container
  // as a RefObject (e.g. `useActiveSection`).
  const rootRef = useRef<HTMLElement | null>(null)
  const detachRef = useRef<(() => void) | null>(null)

  // Callback ref for the comment-anchor host. Attaches the click delegation when
  // the node mounts and detaches when it unmounts. This replaces a one-shot
  // `[rootRef]` effect that only ran at first commit — which missed hosts that
  // render a loading state *before* the scroll container (Present mode), where
  // `rootRef.current` was still null when the effect ran, so the listener never
  // attached and comment triggers were dead.
  const setRoot = useCallback((node: HTMLElement | null) => {
    detachRef.current?.()
    detachRef.current = null
    rootRef.current = node
    if (!node) return

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      // Only open when the user explicitly clicks the comment trigger.
      const trigger = target.closest<HTMLElement>('[data-comment-trigger="true"]')
      if (!trigger) return
      const host = trigger.closest<HTMLElement>('[data-comment-anchor-type]')
      if (!host) return
      const type = host.getAttribute('data-comment-anchor-type') as CommentAnchorType | null
      const id = host.getAttribute('data-comment-anchor-id')
      const label = host.getAttribute('data-comment-label') ?? undefined
      if (!type || !id || !KNOWN_TYPES.includes(type)) return
      e.preventDefault()
      e.stopPropagation()
      setAnchor({ type, id, label })
      setOpen(true)
    }

    node.addEventListener('click', onClick)
    detachRef.current = () => node.removeEventListener('click', onClick)
  }, [])

  const close = useCallback(() => {
    setOpen(false)
    setAnchor(null)
  }, [])

  return { anchor, open, close, setAnchor, setOpen, rootRef, setRoot }
}
